/**
 * Step 1 — workshop identity, hourly rate and bank details (#1853).
 *
 * Deliberately NOT `WorkshopSettingsForm`: that component is 874 lines
 * covering VAT rates, opening hours and hour exceptions, and it mounts four
 * queries of its own. Rendering it here would make the shortest step the
 * longest one. The fields are duplicated; the persistence is not — saving goes
 * through the same `buildWorkshopSettingsPayload` helper, so a field cannot be
 * dropped from the request the way #1795 happened.
 *
 * Two fields justify the whole wizard:
 *  - the hourly rate, whose absence bills work at zero euros with no error;
 *  - the IBAN, which blocks invoice finalization and, until now, had no editor
 *    anywhere in the portal.
 */

import { useState } from 'react';
import { Alert, Box, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { eurosToCents, netToGrossCents } from '@/utils/money';
import type { WorkshopSettings } from '@/types';

/** Matches the API's own default; the rate is entered VAT-inclusive. */
const DEFAULT_VAT_PERCENT = 25.5;

interface WorkshopStepProps {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopStep({ settings, onChange }: WorkshopStepProps) {
  const { t } = useTranslation('onboarding');
  const { t: ts } = useTranslation('settings');

  // Seeded once from props. The wizard remounts this step via a `key` when the
  // loaded settings arrive, so there is no re-seeding effect to go stale.
  const initialGross =
    settings.laborRateGrossCents != null && settings.laborRateGrossCents > 0
      ? settings.laborRateGrossCents
      : netToGrossCents(settings.laborRateCents ?? 0, DEFAULT_VAT_PERCENT);
  const [laborRateInput, setLaborRateInput] = useState(
    initialGross > 0 ? (initialGross / 100).toFixed(2) : '',
  );

  const set = <K extends keyof WorkshopSettings>(key: K, value: WorkshopSettings[K]) =>
    onChange({ ...settings, [key]: value });

  // Flagged on both fields, since the server accepts either (receipt-seller.ts:46).
  const contactMissing = settings.phone.trim() === '' && settings.email.trim() === '';

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('workshop.intro')}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('workshop.name')}
            value={settings.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField
            fullWidth
            label={t('workshop.address')}
            value={settings.address}
            onChange={(e) => set('address', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={t('workshop.postalCode')}
            value={settings.postalCode}
            onChange={(e) => set('postalCode', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('workshop.city')}
            value={settings.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('workshop.businessId')}
            value={settings.businessId}
            onChange={(e) => set('businessId', e.target.value)}
          />
        </Grid>

        {/*
          One of these is required before a payment can be recorded
          (receipt-seller.ts:46). The wizard did not ask for either, so a
          workshop could finish it and still be unable to write a receipt —
          discovered at the counter (#2495). Either satisfies the server, so
          neither is marked required on its own; the hint says so.
        */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('workshop.phone')}
            value={settings.phone}
            onChange={(e) => set('phone', e.target.value)}
            helperText={contactMissing ? t('workshop.contactHint') : undefined}
            error={contactMissing}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            type="email"
            label={t('workshop.email')}
            value={settings.email}
            onChange={(e) => set('email', e.target.value)}
            helperText={contactMissing ? t('workshop.contactHint') : undefined}
            error={contactMissing}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {t('workshop.pricingTitle')}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            type="number"
            label={ts('laborRateInclVat')}
            helperText={t('workshop.laborRateHint')}
            value={laborRateInput}
            onChange={(e) => setLaborRateInput(e.target.value)}
            onBlur={() => {
              // `eurosToCents` rather than `Math.round(euros * 100)`, which is
              // wrong for ~0.57 % of values (docs/NUMERIC-PRECISION.md).
              const cents = eurosToCents(laborRateInput.replace(',', '.')) ?? 0;
              set('laborRateGrossCents', cents);
              setLaborRateInput(cents > 0 ? (cents / 100).toFixed(2) : '');
            }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                endAdornment: <InputAdornment position="end">/ {ts('hour')}</InputAdornment>,
              },
            }}
          />
        </Grid>

        <Grid size={12}>
          {/*
            Split from pricing to match the settings page, where the hourly
            rate lives under Pricing and the IBAN under Billing (#2468).
            Someone who learned the grouping here should find it again there.
          */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {t('workshop.billingTitle')}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('workshop.iban')}
            helperText={t('workshop.ibanHint')}
            value={settings.bankIban ?? ''}
            onChange={(e) => set('bankIban', e.target.value)}
          />
        </Grid>
      </Grid>

      {!laborRateInput && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          {t('workshop.laborRateWarning')}
        </Alert>
      )}
    </Box>
  );
}
